import { useEffect } from "react";
import { Link } from "react-router-dom";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import Base, { ContentWidth } from "../components/basic/Base";
import {
  spacing,
  black,
  darkGrey,
  fontSize,
  fontWeight,
  green,
  grey,
  mediumGrey,
  white,
} from "../theme";

const DOC_TITLE = "Lathund för intervaller | Trirebels";

const IntervalGuidePage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = DOC_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <PageOuter>
      <Base contentWidth={ContentWidth.Narrow}>
        <Article>
          <Title>Lathund för intervaller</Title>
          <Intro>
            Här är bakgrunden till zonerna och passen i{" "}
            <CalcLink to="/intervall-kalkylator">intervallkalkylatorn</CalcLink>
            : vad olika intensiteter tränar, och hur cykel och löpning skiljer
            sig åt.
          </Intro>

          <Toc>
            <TocLink href="#cykling">Cykling</TocLink>
            <TocLink href="#lopning">Löpning</TocLink>
          </Toc>

          <Section id="cykling">
            <H2>Cykling — % av FTP</H2>
            <P>
              FTP (Functional Threshold Power) är användbart för att styra
              intensiteten, men korta intervaller på 30 sekunder till 3 minuter
              är svårare att styra exakt med enbart procent. Där spelar även
              syreupptagning, återhämtning mellan intervallerna och hur många
              repetitioner du gör stor roll.
            </P>
            <P>
              Procenten nedan är riktvärden för väl genomförda intervallpass —
              inte absoluta gränser. För korta intervaller gäller framför allt
              att du ska kunna hålla kvaliteten genom hela passet.
            </P>

            <H3>Snabbtabell</H3>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Intervall</Th>
                    <Th>% av FTP</Th>
                    <Th>Repetitioner</Th>
                    <Th>Effekt</Th>
                  </tr>
                </thead>
                <tbody>
                  <Tr>
                    <Td>30 sek</Td>
                    <Td>150–200 %</Td>
                    <Td>2–3 × 6–10</Td>
                    <Td>Anaerob kapacitet, VO₂-stimulans</Td>
                  </Tr>
                  <Tr>
                    <Td>30/30 sek</Td>
                    <Td>120–150 % / 40–60 %</Td>
                    <Td>2–3 × 8–15 min</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>1 min</Td>
                    <Td>130–160 %</Td>
                    <Td>8–15</Td>
                    <Td>VO₂max, anaerob kapacitet</Td>
                  </Tr>
                  <Tr>
                    <Td>2 min</Td>
                    <Td>120–140 %</Td>
                    <Td>5–8</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>3 min</Td>
                    <Td>115–130 %</Td>
                    <Td>4–6</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>4 min</Td>
                    <Td>108–120 %</Td>
                    <Td>4–5</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>5 min</Td>
                    <Td>105–115 %</Td>
                    <Td>4–6</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>10 min</Td>
                    <Td>95–105 %</Td>
                    <Td>3–4</Td>
                    <Td>Tröskel, hög aerob effekt</Td>
                  </Tr>
                  <Tr>
                    <Td>15 min</Td>
                    <Td>90–100 %</Td>
                    <Td>2–3</Td>
                    <Td>Tröskel, uthållighet</Td>
                  </Tr>
                  <Tr>
                    <Td>20 min</Td>
                    <Td>90–100 %</Td>
                    <Td>2–3</Td>
                    <Td>Tröskel, aerob uthållighet</Td>
                  </Tr>
                </tbody>
              </Table>
            </TableWrap>
            <P>
              Antalen är ungefärliga. Vila, träningsvana och passets totala
              belastning avgör vad som är lämpligt. För 30/30 och
              30-sekundersintervaller avser antalet repetitioner antal
              arbetsperioder.
            </P>

            <H3>Vad tränar du egentligen?</H3>
            <ZoneCard>
              <ZoneTitle>30 sekunder–1 minut</ZoneTitle>
              <ZoneLabel>Anaerob kapacitet och VO₂max</ZoneLabel>
              <P>
                Du tränar förmågan att producera hög effekt och upprepa hårda
                attacker. Vid korta intervaller med kort vila kan
                syreupptagningen också hållas hög mellan repetitionerna. Ren
                sprintträning med full vila har en annan effekt än upprepade
                30/30-intervaller.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>2–5 minuter</ZoneTitle>
              <ZoneLabel>VO₂max</ZoneLabel>
              <P>
                Ett av de mest effektiva intervallformaten för maximal
                syreupptagningsförmåga. Du samlar tid med mycket hög
                syreomsättning. Exempel: 4 × 4 minuter eller 5 × 5 minuter.
                Cirka 15–25 minuter sammanlagd hård arbetstid är ett användbart
                riktmärke för många tränade cyklister.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>8–15 minuter</ZoneTitle>
              <ZoneLabel>Tröskel och sweet spot</ZoneLabel>
              <P>
                Du utvecklar förmågan att hålla hög effekt länge, hantera laktat
                och förbättra uthålligheten nära tröskeln. Intensiteten avgör om
                passet blir sweet spot, tröskel eller över tröskel.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>15–20 minuter</ZoneTitle>
              <ZoneLabel>Tröskel och aerob uthållighet</ZoneLabel>
              <P>
                Du bygger förmågan att hålla en hög, jämn effekt under lång tid.
                Sweet spot ger mycket arbete nära tröskeln med något lägre
                belastning; arbete vid FTP riktas mer direkt mot tröskeleffekten.
              </P>
            </ZoneCard>

            <H3>Zoner i förhållande till FTP</H3>
            <P>
              Det är inte bara intervallängden som bestämmer effekten — det är
              framför allt intensiteten i förhållande till din FTP.
            </P>
            <ZoneCard>
              <ZoneTitle>88–94 %</ZoneTitle>
              <ZoneLabel>Sweet spot</ZoneLabel>
              <P>
                Hårt men kontrollerat. Mycket tid nära tröskeln utan samma
                belastning som rena tröskelintervaller.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>95–105 %</ZoneTitle>
              <ZoneLabel>Tröskel</ZoneLabel>
              <P>
                Förmågan att hålla hög effekt länge. Typiska pass: 3 × 10, 2 × 15
                eller 2 × 20 minuter.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>106–120 %</ZoneTitle>
              <ZoneLabel>VO₂max</ZoneLabel>
              <P>
                Hög intensitet som utvecklar maximal aerob kapacitet. Framför
                allt användbart vid intervaller på cirka 3–8 minuter.
              </P>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>121–150 %+ </ZoneTitle>
              <ZoneLabel>Anaerob kapacitet</ZoneLabel>
              <P>
                Korta, mycket hårda intervaller för attacker, backryck och
                upprepade kraftinsatser. För 30 sekunder kan effekten behöva vara
                ännu högre.
              </P>
            </ZoneCard>
            <P>
              Zonerna överlappar och är ungefärliga. VO₂max-effekt kan ligga
              utanför dessa intervall beroende på cyklist och protokoll.
            </P>

            <H3>Praktiska råd</H3>
            <Ul>
              <Li>
                Börja med 1–2 hårda cykelpass i veckan. Fyll på med lugn distans
                däremellan.
              </Li>
              <Li>
                Öka inte både effekt och antal intervaller samtidigt — lägg
                hellre till en repetition när du klarar passet med jämn kvalitet.
              </Li>
              <Li>
                Använd rätt effekt för rätt syfte. Att köra 20 minuter på 110 %
                av FTP är inte ett normalt tröskelpass.
              </Li>
              <Li>
                På t.ex. 4 × 4 minuter: sikta på jämn, hög effekt genom alla
                fyra. Att köra första för hårt och tappa 10–15 % mot slutet är
                sällan bättre.
              </Li>
              <Li>
                Uppdatera FTP när formen förändras — alla wattmål bygger på att
                den är någorlunda korrekt.
              </Li>
            </Ul>
          </Section>

          <Section id="lopning">
            <H2>Löpning — tävlingsfart, tröskel och tempo</H2>
            <P>
              Löpning går att strukturera på ett liknande sätt som cykling, men
              vi styr med tempo (min/km), puls och upplevd ansträngning — inte
              watt. Översätt inte cykelns FTP-procent rakt av till löpning. Bättre
              är att utgå från din tävlingsfart, tröskelfart och ungefärliga
              VO₂max-fart.
            </P>

            <H3>Snabbtabell</H3>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Intervall</Th>
                    <Th>Intensitet</Th>
                    <Th>Repetitioner</Th>
                    <Th>Effekt</Th>
                  </tr>
                </thead>
                <tbody>
                  <Tr>
                    <Td>30 sek</Td>
                    <Td>1500–3000 m-fart</Td>
                    <Td>10–20</Td>
                    <Td>Löpekonomi, snabbhet</Td>
                  </Tr>
                  <Tr>
                    <Td>1 min</Td>
                    <Td>3–5 km-fart</Td>
                    <Td>8–15</Td>
                    <Td>VO₂max, snabbhet</Td>
                  </Tr>
                  <Tr>
                    <Td>2–4 min</Td>
                    <Td>3–5 km-fart</Td>
                    <Td>4–8</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>5 min</Td>
                    <Td>5 km-fart</Td>
                    <Td>4–5</Td>
                    <Td>VO₂max</Td>
                  </Tr>
                  <Tr>
                    <Td>10 min</Td>
                    <Td>Tröskelfart</Td>
                    <Td>3–4</Td>
                    <Td>Tröskel</Td>
                  </Tr>
                  <Tr>
                    <Td>15 min</Td>
                    <Td>Tröskelfart</Td>
                    <Td>2–3</Td>
                    <Td>Tröskel</Td>
                  </Tr>
                  <Tr>
                    <Td>20 min</Td>
                    <Td>Tröskel / strax under</Td>
                    <Td>2</Td>
                    <Td>Tröskel och uthållighet</Td>
                  </Tr>
                </tbody>
              </Table>
            </TableWrap>
            <P>
              Farten ska anpassas efter din kapacitet och hur lång vila du har
              mellan intervallerna. För VO₂max är 3–5-minutersintervaller
              särskilt användbara; cirka 15–25 minuter sammanlagd intervalltid är
              en bra utgångspunkt.
            </P>

            <H3>Vilken fart — och hur känns det?</H3>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Farttyp</Th>
                    <Th>Känsla</Th>
                    <Th>RPE (1–10)</Th>
                  </tr>
                </thead>
                <tbody>
                  <Tr>
                    <Td>Lätt distans</Td>
                    <Td>Kan prata obehindrat</Td>
                    <Td>2–3</Td>
                  </Tr>
                  <Tr>
                    <Td>Maratonfart</Td>
                    <Td>Kontrollerat, fokuserat</Td>
                    <Td>5–6</Td>
                  </Tr>
                  <Tr>
                    <Td>Tröskelfart</Td>
                    <Td>Hårt men kontrollerat</Td>
                    <Td>7–8</Td>
                  </Tr>
                  <Tr>
                    <Td>10 km-fart</Td>
                    <Td>Hårt, men jämnt</Td>
                    <Td>8</Td>
                  </Tr>
                  <Tr>
                    <Td>5 km-fart</Td>
                    <Td>Mycket hårt</Td>
                    <Td>9</Td>
                  </Tr>
                  <Tr>
                    <Td>VO₂max-intervaller</Td>
                    <Td>Nära max mot slutet</Td>
                    <Td>9–10</Td>
                  </Tr>
                  <Tr>
                    <Td>Sprint</Td>
                    <Td>Maximal eller nära</Td>
                    <Td>10</Td>
                  </Tr>
                </tbody>
              </Table>
            </TableWrap>
            <P>
              Pulsen är ett sämre styrverktyg för korta intervaller — den hinner
              inte reagera tillräckligt snabbt på 30 sekunder eller 1 minut. Där
              ska du främst styra på tempo och känsla. Vid längre intervaller kan
              puls vara ett bra komplement.
            </P>

            <H3>Tröskel, VO₂max och löpekonomi</H3>
            <ZoneCard>
              <ZoneTitle>Tröskelträning</ZoneTitle>
              <ZoneLabel>Kontrollerat hårt</ZoneLabel>
              <Ul>
                <Li>Förbättrar farten du kan hålla under lång tid.</Li>
                <Li>Ökar din förmåga att hantera laktat.</Li>
                <Li>Bra för 10 km, halvmaraton och maraton.</Li>
                <Li>Exempel: 3 × 10 min eller 2 × 20 min.</Li>
              </Ul>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>VO₂max-träning</ZoneTitle>
              <ZoneLabel>Mycket hög intensitet</ZoneLabel>
              <Ul>
                <Li>Förbättrar maximal syreupptagningsförmåga.</Li>
                <Li>
                  Förbättrar hjärtats och musklernas förmåga att arbeta vid hög
                  intensitet.
                </Li>
                <Li>Höjer ditt aeroba tak.</Li>
                <Li>Exempel: 5 × 4 min eller 5 × 5 min.</Li>
              </Ul>
            </ZoneCard>
            <ZoneCard>
              <ZoneTitle>Snabbhet och löpekonomi</ZoneTitle>
              <ZoneLabel>Korta repetitioner</ZoneLabel>
              <Ul>
                <Li>
                  Förbättrar stegfrekvens, koordination och förmågan att springa
                  avslappnat i hög fart.
                </Li>
                <Li>
                  Exempel: 10–15 × 30 sek eller korta backintervaller.
                </Li>
                <Li>
                  30 sek med lång vila → mer snabbhet/anaerobt. 30/30 med kort
                  vila → mer aerob stimulans.
                </Li>
              </Ul>
            </ZoneCard>

            <H3>Praktiska råd</H3>
            <Ul>
              <Li>
                Börja med 1–2 kvalitativa löppass i veckan. Fyll på med lätt
                distans däremellan.
              </Li>
              <Li>
                Öka inte både fart och antal repetitioner samtidigt.
              </Li>
              <Li>
                Uppdatera ditt referenslopp när formen förändras — farttabellen i
                kalkylatorn bygger på aktuell kapacitet.
              </Li>
            </Ul>
          </Section>

          <Cta>
            <P>
              Redo att få ett konkret pass med watt eller min/km utifrån dina
              egna siffror?
            </P>
            <CalcLink to="/intervall-kalkylator">Öppna intervallkalkylatorn</CalcLink>
          </Cta>
        </Article>
      </Base>
    </PageOuter>
  );
};

export default IntervalGuidePage;

const PageOuter = styled.div({
  padding: { top: spacing.xl, bottom: spacing.xxxl, x: spacing.m },
  color: black,
  minHeight: "60vh",
  backgroundColor: white,
});

const Article = styled.article({
  maxWidth: "720px",
  margin: { x: "auto" },
});

const Title = styled.h1({
  fontSize: fontSize.xxl,
  fontWeight: fontWeight.heavy,
  margin: { bottom: spacing.m },
  lineHeight: 1.15,
  ...media({ maxWidth: "767px" }, {
    fontSize: fontSize.xl,
  }),
});

const Intro = styled.p({
  fontSize: fontSize.m,
  color: darkGrey,
  margin: { bottom: spacing.l },
  lineHeight: 1.55,
});

const Toc = styled.nav({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.m,
  marginBottom: spacing.xl,
  paddingBottom: spacing.m,
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const TocLink = styled.a({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
  textDecoration: "underline",
  ":link": { color: black },
  ":visited": { color: black },
  ":hover": { color: black },
  ":active": { color: black },
});

const Section = styled.section({
  margin: { bottom: spacing.xxxl },
  scrollMarginTop: spacing.xl,
});

const H2 = styled.h2({
  fontSize: fontSize.xl,
  fontWeight: fontWeight.heavy,
  margin: { top: 0, bottom: spacing.m },
  color: black,
});

const H3 = styled.h3({
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
  margin: { top: spacing.xl, bottom: spacing.s },
  color: black,
});

const P = styled.p({
  fontSize: fontSize.m,
  lineHeight: 1.6,
  margin: { bottom: spacing.m },
  color: black,
});

const Ul = styled.ul({
  margin: { bottom: spacing.m },
  paddingLeft: spacing.l,
});

const Li = styled.li({
  fontSize: fontSize.m,
  lineHeight: 1.6,
  margin: { bottom: spacing.xs },
});

const CalcLink = styled(Link, {
  color: black,
  fontWeight: fontWeight.bold,
  textDecoration: "underline",
  ":link": { color: black },
  ":visited": { color: black },
  ":hover": { color: black },
  ":active": { color: black },
});

const ZoneCard = styled.div({
  backgroundColor: mediumGrey,
  borderRadius: "10px",
  padding: { xy: spacing.m },
  marginBottom: spacing.m,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const ZoneTitle = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.heavy,
  color: black,
});

const ZoneLabel = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: green,
  margin: { top: spacing.xxs, bottom: spacing.s },
});

const TableWrap = styled.div({
  overflowX: "auto",
  marginBottom: spacing.m,
  WebkitOverflowScrolling: "touch",
});

const Table = styled.table({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.s,
  minWidth: "520px",
});

const Th = styled.th({
  textAlign: "left",
  padding: { x: spacing.s, y: spacing.xs },
  borderBottom: { style: "solid", color: black, width: "2px" },
  fontWeight: fontWeight.bold,
  whiteSpace: "nowrap",
});

const Tr = styled.tr({});

const Td = styled.td({
  padding: { x: spacing.s, y: spacing.xs },
  borderBottom: { style: "solid", color: grey, width: "1px" },
  verticalAlign: "top",
  lineHeight: 1.4,
});

const Cta = styled.div({
  marginTop: spacing.xl,
  padding: { xy: spacing.l },
  backgroundColor: mediumGrey,
  borderRadius: "12px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});
